import React, { useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import PDFBookDisplay from './PDFBookDisplay';
import { ServicePageContent } from '../services/servicePageConfigApi';

interface ServiceContentRendererProps {
  contents: ServicePageContent[];
}

// Composant pour générer un QR code côté client
const QRCodeImage: React.FC<{ data: string; size?: number }> = ({ data, size = 200 }) => {
  const [qrUrl, setQrUrl] = React.useState<string>('');

  useEffect(() => {
    if (data) {
      // Utiliser une API externe pour générer le QR code
      const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}&format=png&margin=10&color=000000&bgcolor=ffffff`;
      setQrUrl(url);
    }
  }, [data, size]);

  return qrUrl ? (
    <img 
      src={qrUrl} 
      alt="QR Code" 
      width={size} 
      height={size}
      className="mx-auto block"
      style={{ imageRendering: 'pixelated' }}
    />
  ) : (
    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
      <span className="text-gray-500 text-xs">Chargement...</span>
    </div>
  );
};

const ServiceContentRenderer: React.FC<ServiceContentRendererProps> = ({ contents }) => {
  
  // Debug logs
  console.log('🎨 ServiceContentRenderer - Contenus reçus:', contents);
  console.log('🎨 Nombre total de contenus:', contents.length);
  
  if (contents.length === 0) {
    return (
      <div className="text-gray-500 text-sm italic">
        Aucun contenu personnalisé configuré pour ce service.
      </div>
    );
  }

  // Séparer les contenus par type
  const textContents = contents.filter(c => c.content_type === 'text');
  const linkContents = contents.filter(c => c.content_type === 'link');
  const visualContents = contents.filter(c => c.content_type === 'image' || c.content_type === 'qr_code' || c.content_type === 'pdf');
  
  // Debug logs pour chaque type
  console.log('📝 Contenus texte:', textContents.length);
  console.log('🔗 Contenus lien:', linkContents.length, linkContents);
  console.log('👁️ Contenus visuels (images/QR/PDF):', visualContents.length);
  
  // Debug spécifique pour les liens
  linkContents.forEach((link, index) => {
    console.log(`🔗 Lien ${index + 1}:`, {
      id: link.id,
      text: link.content_data.link_text,
      url: link.content_data.link_url,
      hasText: !!link.content_data.link_text,
      hasUrl: !!link.content_data.link_url
    });
  });
  
  // Debug spécifique pour les PDF
  const pdfContents = contents.filter(c => c.content_type === 'pdf');
  console.log('📄 Contenus PDF trouvés:', pdfContents.length);
  pdfContents.forEach((pdf, index) => {
    console.log(`📄 PDF ${index + 1}:`, {
      id: pdf.id,
      title: pdf.content_data.pdf_title,
      pdf_id: pdf.content_data.pdf_id,
      description: pdf.content_data.pdf_description
    });
  });

  // Calculer la taille des éléments visuels en fonction du nombre
  const getVisualItemHeight = () => {
    const visualCount = visualContents.length;
    if (visualCount === 0) return 0;
    if (visualCount === 1) return 'h-64'; // Grande taille pour 1 élément
    if (visualCount === 2) return 'h-48'; // Moyenne pour 2 éléments  
    if (visualCount === 3) return 'h-36'; // Plus petite pour 3 éléments
    return 'h-32'; // Très petite pour 4+ éléments
  };

  const getQRSize = () => {
    const visualCount = visualContents.length;
    if (visualCount === 1) return 160;
    if (visualCount === 2) return 128;
    if (visualCount === 3) return 96;
    return 80;
  };

  const visualItemHeight = getVisualItemHeight();
  const qrSize = getQRSize();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      {/* Colonne principale : Textes et liens */}
      <div className="lg:col-span-2 space-y-4 flex flex-col justify-start">
        {/* Textes */}
        {textContents.map((content) => (
          <div key={content.id} className="prose prose-sm max-w-none">
            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {content.content_data.text}
            </div>
          </div>
        ))}

        {/* Liens */}
        {linkContents.length > 0 ? (
          <div className="space-y-3 mt-6">
            <h4 className="text-sm font-semibold text-gray-600 mb-2">🔗 Liens utiles</h4>
            {linkContents.map((content) => {
              // Debug pour chaque lien rendu
              console.log(`🎨 Rendu lien ${content.id}:`, {
                text: content.content_data.link_text,
                url: content.content_data.link_url,
                hasValidUrl: !!content.content_data.link_url,
                hasValidText: !!content.content_data.link_text
              });
              
              return (
                <div key={content.id}>
                  {content.content_data.link_url && content.content_data.link_text ? (
                    <a
                      href={content.content_data.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded-lg border border-blue-200 transition-colors duration-200 text-sm font-medium"
                    >
                      <span>{content.content_data.link_text}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : (
                    <div className="bg-yellow-100 border border-yellow-400 rounded p-2 text-xs">
                      <strong>⚠️ Lien incomplet:</strong>
                      <br />Texte: {content.content_data.link_text || 'Manquant'}
                      <br />URL: {content.content_data.link_url || 'Manquante'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500 italic">
              💡 Aucun lien configuré pour ce service
            </p>
          </div>
        )}

      </div>

      {/* Colonne latérale : Images, QR codes et PDFs */}
      {visualContents.length > 0 && (
        <div className="lg:col-span-1 flex flex-col justify-start space-y-3 h-full">
          {visualContents.map((content, index) => (
            <div key={content.id} className="flex-1 flex items-center justify-center relative">
              {/* Images */}
              {content.content_type === 'image' && content.content_data.image_url && (
                <div className="image-content w-full cursor-pointer group" onClick={() => {
                  console.log('🖼️ Ouverture image:', content.content_data.image_url);
                  window.open(content.content_data.image_url, '_blank');
                }}>
                  <img
                    src={content.content_data.image_url}
                    alt={`Illustration ${index + 1}`}
                    className={`w-full ${getVisualItemHeight()} object-contain rounded-lg shadow-sm transition-transform group-hover:scale-105`}
                  />
                  {/* Tooltip pour l'image */}
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    🖼️ Cliquer pour agrandir
                  </div>
                </div>
              )}

              {/* QR Codes */}
              {content.content_type === 'qr_code' && (content.content_data.qr_data || content.content_data.qr_image_url) && (
                <div className="qr-code-content text-center bg-gradient-to-br from-blue-50 to-indigo-50 p-3 rounded-xl border border-blue-100 w-full cursor-pointer group relative"
                     onClick={() => {
                       const qrImageUrl = content.content_data.qr_mode === 'upload' 
                         ? content.content_data.qr_image_url 
                         : `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(content.content_data.qr_data || '')}`;
                       console.log('📱 Ouverture QR code:', qrImageUrl);
                       window.open(qrImageUrl, '_blank');
                     }}>
                  
                  {/* Message d'instruction */}
                  <p className="text-xs text-blue-600 mb-2 font-medium">
                    📱 Scannez le QR code pour voir
                  </p>
                  
                  {content.content_data.qr_label && (
                    <p className="text-xs text-gray-700 mb-2 font-semibold">
                      {content.content_data.qr_label}
                    </p>
                  )}
                  <div className="inline-block p-2 bg-white rounded-xl border-2 border-gray-200 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                    {/* QR Code uploadé */}
                    {content.content_data.qr_mode === 'upload' && content.content_data.qr_image_url ? (
                      <img
                        src={content.content_data.qr_image_url}
                        alt="QR Code"
                        className="mx-auto block rounded-lg"
                        style={{ 
                          imageRendering: 'pixelated',
                          width: `${qrSize}px`,
                          height: `${qrSize}px`
                        }}
                      />
                    ) : (
                      /* QR Code généré automatiquement */
                      content.content_data.qr_data && (
                        <QRCodeImage data={content.content_data.qr_data} size={qrSize} />
                      )
                    )}
                  </div>
                  
                  {/* Tooltip pour QR code */}
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    🔍 Cliquer pour agrandir le QR code
                  </div>
                </div>
              )}

              {/* PDFs */}
              {content.content_type === 'pdf' && content.content_data.pdf_url && (() => {
                // Debug pour ce PDF spécifique
                console.log(`🔍 Rendu PDF ${content.id}:`, {
                  type: content.content_type,
                  title: content.content_data.pdf_title,
                  url: content.content_data.pdf_url,
                  description: content.content_data.pdf_description,
                  hasTitle: !!content.content_data.pdf_title,
                  hasUrl: !!content.content_data.pdf_url
                });
                
                return (
                  <PDFBookDisplay
                    title={content.content_data.pdf_title || 'Document PDF'}
                    description={content.content_data.pdf_description}
                    pdfUrl={content.content_data.pdf_url}
                    size={visualContents.length === 1 ? 'large' : visualContents.length <= 3 ? 'medium' : 'small'}
                  />
                );
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceContentRenderer;
